import {
	Column,
	Entity,
	Index,
	JoinColumn,
	ManyToOne,
	OneToMany,
} from 'typeorm';
import { BaseEntity } from './base.entity';
import { CompanyEntity } from './company.entity';
import { PortalSiteDomainEntity } from './portal-site-domain.entity';

/**
 * Persisted shape of `portal_site.settings`. All fields optional — missing
 * keys take the resolver's hardcoded defaults (see resolvePortalSiteSettings).
 *
 * Stock is split into two independent toggles per visitor type:
 *   - **availability**: the In Stock / Out of Stock pill (boolean shape).
 *     Cheap to expose and rarely sensitive — defaults to true for auth.
 *   - **quantity**: the actual number on hand. Often considered competitively
 *     sensitive — defaults to false even for auth so admins opt in.
 *
 * Quantity should only be considered visible if availability is also on;
 * the resolver treats quantity-without-availability as effectively false.
 *
 * Legacy keys (`anonymousShowStock`, `showStockToAuthenticated`) are still
 * read for back-compat with rows written before the split — see
 * `resolveEffectivePortalSiteSettings` for the migration logic.
 */
export interface PortalSiteSettings {
	/** Allow unauthenticated visitors to browse the catalog at all. Default false. */
	allowAnonymousBrowsing?: boolean;
	/** Show price to unauthenticated visitors. Only meaningful when anonymous browsing is allowed. Default false. */
	anonymousShowPricing?: boolean;
	/** Show "In Stock / Out of Stock" availability to anonymous visitors. Default false. */
	anonymousShowStockAvailability?: boolean;
	/** Show numeric quantity on hand to anonymous visitors. Default false; only meaningful when availability is also on. */
	anonymousShowStockQuantity?: boolean;
	/** Show "In Stock / Out of Stock" availability to authenticated users. Default true. */
	showStockAvailabilityToAuthenticated?: boolean;
	/** Show numeric quantity on hand to authenticated users. Default false; only meaningful when availability is also on. */
	showStockQuantityToAuthenticated?: boolean;

	// --- Authenticated-experience toggles (catalog-only-mode storefronts) ---
	/**
	 * Allow authenticated users to add to cart and place orders. Some
	 * customers want a "browse-only" portal where logged-in users can see
	 * prices/stock but cannot purchase (e.g. quote-driven sales). Default
	 * true.
	 */
	allowPurchasing?: boolean;
	/** Allow authenticated users to view their order history. Default true. */
	allowOrderHistory?: boolean;
	/** Allow authenticated users to view their invoices. Default true. */
	allowInvoiceViewing?: boolean;
	/**
	 * Allow authenticated users to pay invoices through the portal. Default
	 * true. Auto-coerced to false at runtime when no payment gateway is
	 * configured for the portal's company — the UI must render that case
	 * the same as the admin-disabled case.
	 */
	allowInvoicePayments?: boolean;
	/**
	 * Where authenticated users land after sign-in. `'browse'` drops them
	 * straight into the catalog (current default behavior); `'dashboard'`
	 * shows an action-tile landing page. Default `'browse'`.
	 */
	defaultAuthenticatedView?: 'browse' | 'dashboard';

	// --- Legacy keys (back-compat for rows written before the avail/qty split) ---
	/** @deprecated use anonymousShowStockAvailability + anonymousShowStockQuantity */
	anonymousShowStock?: boolean;
	/** @deprecated use showStockAvailabilityToAuthenticated + showStockQuantityToAuthenticated */
	showStockToAuthenticated?: boolean;
}

/**
 * A `portal_site` is the storefront identity for a single company in a
 * tenant — 1:1 with `company`. There's no notion of a brand spanning
 * multiple companies; "brand" is purely UI sugar over a company's
 * storefront (alias for the URL, logo, email logo, theming).
 *
 * `isDefault` only means "this brand can be reached via the bare
 * <tenant>.my.workd.com URL too" — every brand still has its own alias
 * and the prefixed URL works in addition. There is no brand fallback
 * routing: an unmatched alias is a 404, full stop.
 */
@Index('UK_portal_site_alias', ['alias'], { unique: true })
@Index('UK_portal_site_company', ['fkCompanyId'], { unique: true })
@Entity('portal_site')
export class PortalSiteEntity extends BaseEntity {
	// Required for every brand. Lowercased [a-z0-9]+ (no dashes — dash is
	// the brand/tenant delimiter in the URL). Unique across the tenant.
	@Column('varchar', { name: 'alias', length: 32 })
	public alias!: string;

	// 1:1 with company. Unique across the tenant — exactly one storefront
	// per company. Default 0 so newly-synced rows aren't immediately
	// constraint-violating; service layer requires a real id on create.
	@Column('int', {
		name: 'fk_company__id',
		unsigned: true,
		default: 0,
	})
	public fkCompanyId!: number;

	@Column('varchar', { name: 'displayName', length: 128 })
	public displayName!: string;

	@Column('tinyint', {
		name: 'isActive',
		width: 1,
		default: () => "'1'",
	})
	public isActive!: boolean;

	// "Bare URL maps to this brand too." At most one row per tenant should
	// have isDefault = 1. The aliased URL still works on default rows; this
	// is purely a "no prefix needed" convenience for single-brand tenants
	// (and the tenant's chosen primary brand in multi-brand setups).
	@Column('tinyint', {
		name: 'isDefault',
		width: 1,
		default: () => "'0'",
	})
	public isDefault!: boolean;

	// Brand wordmark for dark-mode / dark-surface contexts (the storefront
	// header is painted with primaryColor and stays dark in both modes, so
	// this is the "default" logo for that surface). Tenants typically upload
	// a white/light wordmark here. Falls back to itself in light mode if
	// `logoUrlLight` is unset, so existing single-asset tenants keep working.
	@Column('varchar', { name: 'logoUrl', nullable: true, length: 512 })
	public logoUrl!: string | null;

	// Brand wordmark variant for light-mode surfaces — used post-login when
	// the tenant has opted into a light mode that paints surfaces white,
	// where a white-on-dark wordmark would disappear. Optional: when null,
	// consumers fall back to `logoUrl`.
	@Column('varchar', { name: 'logoUrlLight', nullable: true, length: 512 })
	public logoUrlLight!: string | null;

	@Column('varchar', { name: 'emailLogoUrl', nullable: true, length: 512 })
	public emailLogoUrl!: string | null;

	/**
	 * Storefront-scoped ecommerce settings. JSON column so we can evolve
	 * without a migration per knob. Fields are intentionally optional in
	 * the type so older rows (stored as `null`) and partial saves both
	 * work; the resolver applies hardcoded defaults for any missing key.
	 *
	 * The API NEVER trusts a client-provided "show price" flag — it always
	 * reads this column server-side after middleware resolves the active
	 * portal_site from the request hostname.
	 */
	@Column('json', { name: 'settings', nullable: true })
	public settings!: PortalSiteSettings | null;

	// Hero image rendered behind the storefront LoginModal (right-side brand
	// panel of the split-pane sign-in screen, full-bleed background on
	// mobile/Capacitor). CDN URL produced by PublicCdnUploadService.
	@Column('varchar', {
		name: 'loginBackgroundUrl',
		nullable: true,
		length: 512,
	})
	public loginBackgroundUrl!: string | null;

	// Storefront favicon. Swapped into <head> on tenant resolution so the
	// browser tab shows the brand mark instead of the generic Workd icon.
	@Column('varchar', { name: 'faviconUrl', nullable: true, length: 512 })
	public faviconUrl!: string | null;

	// Brand primary color as a CSS-compatible hex string (e.g. '#0F1A2A').
	// Surfaced to the storefront via CSS custom properties so Tailwind
	// utilities like `bg-brand-primary` can resolve at runtime per tenant.
	// This is the dark-mode / default value; `primaryColorLight` overrides
	// it post-login when the user has opted into light mode.
	@Column('varchar', { name: 'primaryColor', nullable: true, length: 16 })
	public primaryColor!: string | null;

	// Light-mode override for `primaryColor`. Optional: when null, consumers
	// fall back to `primaryColor` so existing single-color tenants keep
	// working. Only applies post-login (login/welcome/register stay on the
	// default primaryColor).
	@Column('varchar', {
		name: 'primaryColorLight',
		nullable: true,
		length: 16,
	})
	public primaryColorLight!: string | null;

	// Brand accent color as a CSS-compatible hex string (e.g. '#EF6C2D').
	// Used for vibrant CTAs, eyebrows, and energetic moments — paired with
	// primaryColor through the runtime theme provider. This is the
	// dark-mode / default value.
	@Column('varchar', { name: 'accentColor', nullable: true, length: 16 })
	public accentColor!: string | null;

	// Light-mode override for `accentColor`. Optional: when null, consumers
	// fall back to `accentColor`.
	@Column('varchar', {
		name: 'accentColorLight',
		nullable: true,
		length: 16,
	})
	public accentColorLight!: string | null;

	// Page background color in DARK mode (the body / scroll surface behind
	// cards, modals, and the bottom nav). When null, BrandThemeApplier
	// auto-derives a brand-tinted dark from `primaryColor` over near-black
	// so a tenant who only configures `primaryColor` still gets a coherent,
	// non-generic dark mode for free. Configure explicitly for tenants who
	// want a specific dark surface color (e.g. HF Foods sets a deep green
	// here so dark mode reads as "darker HF" instead of generic gray).
	@Column('varchar', {
		name: 'pageBackgroundColor',
		nullable: true,
		length: 16,
	})
	public pageBackgroundColor!: string | null;

	// Page background color in LIGHT mode. When null, BrandThemeApplier
	// auto-derives a subtle brand-tinted off-white from `primaryColor`
	// (4% mix over white) — the existing behavior — so tenants who
	// haven't configured a light page bg keep their current look.
	@Column('varchar', {
		name: 'pageBackgroundColorLight',
		nullable: true,
		length: 16,
	})
	public pageBackgroundColorLight!: string | null;

	// Surface (card / modal / drawer) background color in DARK mode. When
	// null, BrandThemeApplier auto-derives a slightly-lighter-than-page
	// brand-tinted dark from `primaryColor` over near-black so cards
	// always sit visually above the page surface. Configure explicitly
	// when a tenant has a designed elevated-surface color.
	@Column('varchar', {
		name: 'surfaceBackgroundColor',
		nullable: true,
		length: 16,
	})
	public surfaceBackgroundColor!: string | null;

	// Surface (card / modal / drawer) background color in LIGHT mode.
	// When null, defaults to plain white — the long-standing storefront
	// card style. Configure explicitly when a tenant wants tinted cards
	// in light mode (e.g. a soft cream that picks up the page bg tint).
	@Column('varchar', {
		name: 'surfaceBackgroundColorLight',
		nullable: true,
		length: 16,
	})
	public surfaceBackgroundColorLight!: string | null;

	// Solid color shown around/behind the loginBackgroundUrl image whenever
	// the image's aspect ratio doesn't fill the viewport (tall portrait
	// screens, ultra-wide monitors). Should match the dominant edge color
	// of `loginBackgroundUrl` so the transition reads as one continuous
	// scene instead of an image floating on a generic background.
	// Fallback when unset: primaryColor.
	@Column('varchar', {
		name: 'loginBackgroundColor',
		nullable: true,
		length: 16,
	})
	public loginBackgroundColor!: string | null;

	// Master switch for self-serve customer registration on this portal.
	// Off by default — leaving a public write endpoint open on a tenant
	// who hasn't asked for it is a footgun. When false, the public
	// /portal/register endpoint refuses submissions for this brand AND the
	// "Register" link in the storefront login UI is hidden.
	@Column('tinyint', {
		name: 'isRegistrationEnabled',
		width: 1,
		default: () => "'0'",
	})
	public isRegistrationEnabled!: boolean;

	// Per-portal override for the email recipient who gets notified when a
	// new lead lands via /portal/register. Falls back to the tenant-level
	// `notifications.lead_registration_email` instance setting when null,
	// then to a system default if that is also unset.
	@Column('varchar', {
		name: 'leadNotificationEmail',
		nullable: true,
		length: 255,
	})
	public leadNotificationEmail!: string | null;

	// JSON array of fully-qualified origin strings (scheme + host + optional
	// port — e.g. ["https://hffoodsgroup.com"]) that may POST to the public
	// /portal/register endpoint on behalf of this brand from outside our
	// own subdomain. Tenants register their own marketing site origin here
	// so they can host a branded form that submits cross-origin. Our own
	// portal subdomain is auto-trusted server-side and need not be listed.
	// Null/empty means "only our own subdomain may submit."
	@Column('json', { name: 'registrationAllowedOrigins', nullable: true })
	public registrationAllowedOrigins!: Array<string> | null;

	@ManyToOne(() => CompanyEntity, {
		onDelete: 'RESTRICT',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([{ name: 'fk_company__id', referencedColumnName: 'id' }])
	public fkCompany!: CompanyEntity;

	@OneToMany(
		() => PortalSiteDomainEntity,
		(portalSiteDomain) => portalSiteDomain.fkPortalSite,
	)
	public portalSiteDomains!: Array<PortalSiteDomainEntity>;
}
